import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Panel, PanelHeader, formatDateTime } from "./ui";

type Copy = { status: "listing" | "copying" | "done" | "stopped" | "failed"; total: number; copied: number; skipped: number; failed: number; bytes: number; error: string | null; startedAt: string; finishedAt: string | null };

/** Files and photos: copied from JobNimbus onto the imported jobs and contacts, in the background. */
export function FileCopyStatus() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["file-copy"], queryFn: () => get<Copy | null>("/api/import/jobnimbus/files"), refetchInterval: (x) => (x.state.data && ["listing", "copying"].includes(x.state.data.status) ? 3000 : false) });
  const set = (d: Copy | null) => qc.setQueryData(["file-copy"], d);
  const stop = useMutation({ mutationFn: () => post<Copy>("/api/import/jobnimbus/files/stop"), onSuccess: set });
  const c = q.data;
  if (!c) return null;
  const running = !!c && ["listing", "copying"].includes(c.status);
  const left = c ? Math.max(0, c.total - c.skipped - c.copied - c.failed) : 0;
  return (
    <Panel>
      <PanelHeader
        title="Files and photos"
        description="JobNimbus's files and photos, copied onto the imported jobs and contacts in the background. Each Apply on Sync copies what's new and picks up where a stopped copy left off."
        action={
          running && (
            <Button className="px-3 py-1.5" busy={stop.isPending} onClick={() => stop.mutate()}>
              Stop
            </Button>
          )
        }
      />
      <div className="px-5 py-4 text-sm">
        {stop.error && <Alert>{errorMessage(stop.error)}</Alert>}
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Badge tone={c.status === "done" ? "ok" : c.status === "failed" ? "off" : running ? "brand" : "wait"}>{c.status === "listing" ? "Listing files" : c.status === "copying" ? "Copying" : c.status === "done" ? "Done" : c.status === "stopped" ? "Stopped" : "Failed"}</Badge>
              <span>{c.copied.toLocaleString("en-US")} copied ({(c.bytes / 1e9).toFixed(2)} GB)</span>
              {c.status !== "listing" && <span className="text-muted">{left.toLocaleString("en-US")} to go</span>}
              <span className="text-muted">{c.skipped.toLocaleString("en-US")} already copied or not imported</span>
              {c.failed > 0 && <span className="text-danger">{c.failed} failed (the next Apply retries them)</span>}
            </div>
            {c.total > 0 && (
              <div className="h-2 overflow-hidden rounded-full bg-mist">
                <div className="h-full bg-brand transition-all" style={{ width: `${Math.round(((c.copied + c.skipped + c.failed) / c.total) * 100)}%` }} />
              </div>
            )}
            {c.error && <span className="text-muted">{c.error}</span>}
            <span className="text-xs text-muted">Started {formatDateTime(c.startedAt)}{c.finishedAt && `, finished ${formatDateTime(c.finishedAt)}`}</span>
        </div>
      </div>
    </Panel>
  );
}

